import * as Schema from "effect/Schema";

import { TrimmedNonEmptyString } from "./baseSchemas";

/**
 * `server.discoverSourceControl`, which the vendored mobile client calls every
 * time a thread opens.
 *
 * It asks what this host can do about version control: which VCS binaries exist,
 * which of them Forge actually implements, and whether a hosting provider's CLI
 * is present and signed in. Forge had no equivalent, so every thread open logged
 * an error.
 *
 * The shapes mirror `packages/t3-contracts/src/sourceControl.ts`, because the
 * mobile client decodes with those. `Schema.Option` is used where the vendored
 * schema uses it, so the wire encoding matches without a translation step.
 *
 * Forge answers only for what it actually probes — git and jj as version control
 * systems, GitHub as a provider. Claiming "missing" for tools it never looked for
 * would be a worse answer than not listing them.
 */

const SourceControlDiscoveryStatus = Schema.Literals(["available", "missing"]);

const SourceControlProviderAuthStatus = Schema.Literals([
  "authenticated",
  "unauthenticated",
  "unknown",
]);

const VcsDriverKind = Schema.Literals(["git", "jj", "unknown"]);

const SourceControlProviderKind = Schema.Literals([
  "github",
  "gitlab",
  "azure-devops",
  "bitbucket",
  "unknown",
]);

const DiscoverySharedFields = {
  label: TrimmedNonEmptyString,
  executable: Schema.optional(TrimmedNonEmptyString),
  status: SourceControlDiscoveryStatus,
  version: Schema.Option(TrimmedNonEmptyString),
  installHint: TrimmedNonEmptyString,
  detail: Schema.Option(TrimmedNonEmptyString),
} as const;

export const VcsDiscoveryItem = Schema.Struct({
  kind: VcsDriverKind,
  /** Whether Forge can drive it, which is narrower than whether it is installed. */
  implemented: Schema.Boolean,
  ...DiscoverySharedFields,
});
export type VcsDiscoveryItem = typeof VcsDiscoveryItem.Type;

export const SourceControlProviderAuth = Schema.Struct({
  status: SourceControlProviderAuthStatus,
  account: Schema.Option(TrimmedNonEmptyString),
  host: Schema.Option(TrimmedNonEmptyString),
  detail: Schema.Option(TrimmedNonEmptyString),
});
export type SourceControlProviderAuth = typeof SourceControlProviderAuth.Type;

export const SourceControlProviderDiscoveryItem = Schema.Struct({
  kind: SourceControlProviderKind,
  ...DiscoverySharedFields,
  auth: SourceControlProviderAuth,
});
export type SourceControlProviderDiscoveryItem = typeof SourceControlProviderDiscoveryItem.Type;

export const SourceControlDiscoveryResult = Schema.Struct({
  versionControlSystems: Schema.Array(VcsDiscoveryItem),
  sourceControlProviders: Schema.Array(SourceControlProviderDiscoveryItem),
});
export type SourceControlDiscoveryResult = typeof SourceControlDiscoveryResult.Type;
